import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

export const ExpenseItemsEditor = ({
  items = [],
  onAddItem,
  onRemoveItem,
  onItemChange,
  totalAmount = '0.00',
  testIdPrefix = 'expense-items-editor',
  className = '',
  inventoryEnabled = false,
  products = [],
  locations = [],
  showInventoryReadOnly = false,
}) => (
  <section className={`rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4 ${className}`.trim()} data-testid={testIdPrefix}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artigos do documento</p>
        <h3 className="mt-1 text-base font-semibold text-slate-950">Linhas compradas para a loja</h3>
        <p className="mt-1 text-xs text-muted-foreground">Cabeçalho único, mais espaço útil e melhor leitura operacional por linha.</p>
        {inventoryEnabled && (
          <p className="mt-1 text-xs text-blue-700" data-testid={`${testIdPrefix}-inventory-enabled-note`}>
            PGT01 activo: pode ligar linhas ao inventário. O stock só entra após confirmação separada de recepção.
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="outline" data-testid={`${testIdPrefix}-count-badge`}>{items.length} artigo(s)</Badge>
        <Badge variant="outline" data-testid={`${testIdPrefix}-amount-badge`}>{totalAmount} MTn</Badge>
        <Button type="button" variant="outline" className="h-8 rounded-lg px-3" onClick={onAddItem} data-testid={`${testIdPrefix}-add-button`}>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar artigo
        </Button>
      </div>
    </div>

    <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white">
      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-3 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty-state`}>
          Sem linhas ainda. Pode manter uma despesa simples ou detalhar os artigos do documento.
        </div>
      ) : (
        <div className="min-w-[1280px]">
          <div className="grid grid-cols-[minmax(0,2fr)_80px_96px_96px_118px_108px_118px_96px_minmax(0,1.35fr)_minmax(0,1.15fr)_140px_44px] gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500" data-testid={`${testIdPrefix}-header-row`}>
            <span>Artigo</span>
            <span>Unid.</span>
            <span>Qtd</span>
            <span>Aplicada</span>
            <span>Preço</span>
            <span>Saldo</span>
            <span>Total</span>
            <span>Inventário</span>
            <span>Produto</span>
            <span>Armazém</span>
            <span>Estado</span>
            <span></span>
          </div>
          {items.map((item, index) => (
            <div key={item.id || index} className="grid grid-cols-[minmax(0,2fr)_80px_96px_96px_118px_108px_118px_96px_minmax(0,1.35fr)_minmax(0,1.15fr)_140px_44px] gap-2 border-b border-slate-100 px-3 py-2.5 last:border-b-0" data-testid={`${testIdPrefix}-row-${index}`}>
              <Input aria-label={`Artigo ${index + 1}`} value={item.description} onChange={(event) => onItemChange(item.id, 'description', event.target.value)} placeholder="Ex: Cimento 42.5" data-testid={`${testIdPrefix}-description-${index}`} className="h-9 rounded-lg border-slate-300 bg-white" />
              <Input aria-label={`Unidade ${index + 1}`} value={item.unit} onChange={(event) => onItemChange(item.id, 'unit', event.target.value)} placeholder="un" data-testid={`${testIdPrefix}-unit-${index}`} className="h-9 rounded-lg border-slate-300 bg-white" />
              <Input aria-label={`Quantidade ${index + 1}`} type="number" min="0" step="0.001" value={item.quantity} onChange={(event) => onItemChange(item.id, 'quantity', event.target.value)} data-testid={`${testIdPrefix}-quantity-${index}`} className="h-9 rounded-lg border-slate-300 bg-white" />
              <Input aria-label={`Aplicada ${index + 1}`} type="number" min="0" step="0.001" value={item.used_quantity} onChange={(event) => onItemChange(item.id, 'used_quantity', event.target.value)} data-testid={`${testIdPrefix}-used-${index}`} className="h-9 rounded-lg border-slate-300 bg-white" />
              <Input aria-label={`Preço ${index + 1}`} type="number" min="0" step="0.01" value={item.unit_price} onChange={(event) => onItemChange(item.id, 'unit_price', event.target.value)} data-testid={`${testIdPrefix}-unit-price-${index}`} className="h-9 rounded-lg border-slate-300 bg-white" />
              <div className="flex h-9 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700" data-testid={`${testIdPrefix}-remaining-${index}`}>
                {item.remaining_quantity || '0'} {item.unit || 'un'}
              </div>
              <div className="flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-emerald-700" data-testid={`${testIdPrefix}-line-total-${index}`}>
                {item.total || '0.00'} MTn
              </div>
              <div className="flex h-9 items-center justify-center" data-testid={`${testIdPrefix}-inventory-toggle-wrap-${index}`}>
                <Button
                  type="button"
                  variant={item.inventory_linked ? 'default' : 'outline'}
                  className="h-8 rounded-full px-3 text-[11px]"
                  onClick={() => inventoryEnabled && !showInventoryReadOnly && onItemChange(item.id, 'inventory_linked', !item.inventory_linked)}
                  disabled={!inventoryEnabled || showInventoryReadOnly}
                  data-testid={`${testIdPrefix}-inventory-toggle-${index}`}
                >
                  {item.inventory_linked ? 'Ligada' : 'Livre'}
                </Button>
              </div>
              <Select value={item.inventory_product_id || ''} onValueChange={(value) => onItemChange(item.id, 'inventory_product_id', value)} disabled={!inventoryEnabled || !item.inventory_linked || showInventoryReadOnly}>
                <SelectTrigger className="h-9 rounded-lg border-slate-300 bg-white" data-testid={`${testIdPrefix}-inventory-product-${index}`}>
                  <SelectValue placeholder="Produto inventário" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={item.inventory_location_id || ''} onValueChange={(value) => onItemChange(item.id, 'inventory_location_id', value)} disabled={!inventoryEnabled || !item.inventory_linked || showInventoryReadOnly}>
                <SelectTrigger className="h-9 rounded-lg border-slate-300 bg-white" data-testid={`${testIdPrefix}-inventory-location-${index}`}>
                  <SelectValue placeholder="Armazém/local" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex h-9 items-center justify-center">
                <Badge variant="outline" data-testid={`${testIdPrefix}-inventory-status-${index}`}>
                  {item.inventory_status === 'received' ? 'Recebida' : item.inventory_status === 'sync_error' ? 'Erro' : item.inventory_status === 'reversal_blocked' ? 'Bloqueada' : item.inventory_linked ? 'Pendente' : 'Não ligada'}
                </Badge>
              </div>
              <Button type="button" variant="ghost" className="h-9 w-9 rounded-lg text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => onRemoveItem(item.id)} data-testid={`${testIdPrefix}-remove-${index}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  </section>
);